import {Injectable} from "@angular/core";
import {IRecognizedItem, IRecognizedSequence} from "../recognition/recognizer.service";
import {IExportDictionary} from "../../api/data-model";
import {CssSentence, CssWord} from "../../content/content-view.objects";
import {ReviewWordColor} from "../usermodel/usermodel.objects";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";

// TBD: make an object api for the scoring data
// don't think this enum will work - can't do objects
export enum InterventionStatus {
    NONE = 0,
    VISUAL = 1,
    AUDIO = 2
}

export interface IRecordingComparisonResult {
    id: string; // word id of last "good" match
    multiWordInternalProgress:boolean;
    accelerateIntervention:boolean;
    resultDetail: PathElementData[]; // for automated testing
    resultString?: string; // for debugging
}

export interface IReferenceTextData {
    word: CssWord;
    isRequired: boolean;
    interventionStatus: InterventionStatus;
    alternates: ITextWord[][];
    // to be filled in as a result of comparison data
    wordColor: ReviewWordColor;
    elapsedTime: number;
}

export interface IComparison {
    bestTextWord: ITextWord;
    lastTextWord: ITextWord;
}

interface ITextWord {
    id: string;
    isRequired: boolean;
    text: string;
    isLastWord: boolean;
    previousTextWord: ITextWord;
    costArray: PathElementData[];
}

interface IPathStepType {
    name: string;
    delta_recognition: number;
    delta_reference: number;
}

interface IScoreResult {
    numMatched?: number;
    lastTextWord?: ITextWord;
    matchedTextWord: ITextWord;
    exitTextWord?: ITextWord;
}

interface IPostProcessData {
    [wordId: string]: IWordPostProcessData;
}

interface IWordPostProcessData {
    numSyllables: number;
    elapsedTime: number;
}

export class PathElementData {
    // TBD more refactoring here, push some logic into this class or create getters?
    public cost:number;
    public bestPathStep:IPathStepType;
    public recognitionEndMSec:number;
    public textWordId:string;
    public isLastWord:boolean;
    public recognitionItemId:string;
    public recognitionPronNum: number;
    public text:string; // human readable text; above items are ids

    constructor (cost:number, initialPathStep:IPathStepType) {
        this.cost = cost;
        this.bestPathStep = initialPathStep;
        this.recognitionEndMSec = -1;
        this.textWordId = "";
        this.isLastWord = false;
        this.recognitionItemId = "";
        this.recognitionPronNum = 1; // 1-based as returned from recognition
        this.text = ""; // human readable text; above items are ids

    }
    public isMatch():boolean {
        return (this.bestPathStep.name === "diagonal" && this.recognitionItemId === this.text);
    }
}

@Injectable()
export class RecordingComparisonService {
    private pathStep = {
        UNKNOWN: { name: "unknown", delta_reference: 0, delta_recognition: 0 },
        DIAGONAL: { name: "diagonal", delta_reference: -1, delta_recognition: -1 }, // could be match or substitution
        DELETION: { name: "deletion", delta_reference: -1, delta_recognition: 0 },
        INSERTION: { name: "insertion", delta_reference: 0, delta_recognition: -1 }
    };

    private _sentenceTextData:IReferenceTextData[] = []; // entire sentence; used to generate the this.referenceTextData
    private currentRecognitionData:IRecognizedItem[] = [];
    private pronunciationDictionary:IExportDictionary;
    private matchedLastTime = {};
    private referenceTextData:IReferenceTextData[] =
        [{ word: null, isRequired: false, interventionStatus: InterventionStatus.NONE,
            alternates: [[]], wordColor: null, elapsedTime: 0}];
    // just the part we are comparing to the recognition.  if there has been
    // an intervention, this will only be the part of the sentence from the (most recent) intervention onward.
    private threshold:number = 1000;
    private laterMatchSyllableThreshold:number = 3;
    private vowels:string[] = ["AA", "AE", "AH", "AO", "AW", "AY", "EH", "ER", "EY", "IH", "IY", "UH", "UW"];
    private bestPathStep:string = "";
    private textWordId:string = "";
    private isLastWord:boolean = false;

    constructor(private log:BugsnagLoggingService) {

    }

    public createSentence(sentence:CssSentence, pronunciationDictionary: IExportDictionary):void {
        this.clearSentenceText();
        this.pronunciationDictionary = pronunciationDictionary;
        sentence.words.forEach((word:CssWord) => {
            this.addSentenceWord(word);

        });

    }

    public clearSentenceText():void {
        // reset
        this.log.log("clearing sentence text");
        this._sentenceTextData = [];
    }

    get sentenceTextData():IReferenceTextData[] {
        return this._sentenceTextData;
    }

    private addSentenceWord(word:CssWord):void {
        // TBD: move category logic into CssWord
        this.log.log("adding word: " + word.word + " to _sentenceTextData");
        let isRequired:boolean = word.isRequired();
        // TBD: do text, alternates, remove phonemes
        let alternates:ITextWord[][] = [];
        if (word.dictionary) {
            word.dictionary.forEach((alternate:string) => {
                let currentAlternate:ITextWord[] = [];
                alternate.split(" ").forEach((alternateWord:string) => {
                    currentAlternate.push({text: alternateWord.toUpperCase(), id: word.id, isRequired: isRequired,
                                              isLastWord: false, previousTextWord: null, costArray: null});

                });
                currentAlternate[currentAlternate.length - 1].isLastWord = true;
                alternates.push(currentAlternate.slice(0));

            });

        } else {
            alternates = [[{text: word.word.toUpperCase(), id: word.id, isRequired: isRequired,
                isLastWord: true, previousTextWord: null, costArray: null}]];
        }
        this._sentenceTextData.push({ word: word, isRequired: isRequired, interventionStatus: InterventionStatus.NONE,
                                        alternates: alternates, wordColor:ReviewWordColor.GREAT_JOB, elapsedTime: 0});
    }

    public addIntervention(wordId:string, isAudioIntervention:boolean):void {

        this._sentenceTextData.forEach((sentenceTextItem: IReferenceTextData) => {
            // word status list has top-level id, reference text data has subwords split out id-1-2, etc
            if (sentenceTextItem.word && (wordId === sentenceTextItem.word.id)) {
                sentenceTextItem.interventionStatus = isAudioIntervention ?
                                                      InterventionStatus.AUDIO : InterventionStatus.VISUAL;
                // since we will not require that we hear the word after an audio intervention,
                // set isRequired false in that case
                if (isAudioIntervention) {
                    sentenceTextItem.isRequired = false;
                    sentenceTextItem.alternates.forEach((alternate:ITextWord[]) => {
                        alternate.forEach((textWord:ITextWord) => {
                            textWord.isRequired = false;
                        });

                    });
                }

            }

        });

    }

    // for now, map into what the recording controller expects.  Hope to change this going forward.
    // TBD: is this how we still want to do it?
    // need to return: last matched word id (or cssword?); flag to reset timers (internal prog);
    // flag if need accel intervention
    public getComparisonResult(recognitionResult:IRecognizedSequence):IRecordingComparisonResult {
        let result:IComparison = this.makeComparison(recognitionResult);
        let bestPath:PathElementData[] = this.getBestPath(result.bestTextWord);
        let meetsCutoffRequirements = true;
        let lastMatchEndMSec = 0;
        let penultimateMatchedWordId = "";
        bestPath.forEach((entry) => {
            if (entry.textWordId && entry.isMatch()) {
                if (entry.textWordId !== result.bestTextWord.id) {
                    penultimateMatchedWordId = entry.textWordId;
                }
                lastMatchEndMSec = entry.recognitionEndMSec;

            }
        });
        // two cases where we don't want to return the final matched id:
        // 1) we are not at the end of a multi-word sequence yet, and
        // 2) we are at the last word of the sentence and this is the first time recognizing it
        // (due to audio cutoff issues)
        let multiWordIndex = bestPath.filter((pathElement) => {
            return pathElement.textWordId === result.bestTextWord.id;
        }).length - 1;
        let key = result.bestTextWord.id + "_" + multiWordIndex.toString();
        if (!this._sentenceTextData || (this.sentenceTextData.length === 0)) {
            this.log.log("_sentenceTextData is empty!");
        } else {
            if (result.bestTextWord.id === this._sentenceTextData[this._sentenceTextData.length - 1].word.id) {
                meetsCutoffRequirements = recognitionResult.endOfFile ||
                                          this.matchedLastTime[key]
                                          && this.matchedLastTime[key].matched;
            }
        }
        let id = result.bestTextWord.isLastWord && meetsCutoffRequirements
            ? result.bestTextWord.id : penultimateMatchedWordId;
        let multiWordInternalProgress:boolean = !this.matchedLastTime[key]
                                                && !result.bestTextWord.isLastWord;

        // note id could be either behind or ahead of current id (poss shrinkage)
        // resetintervention timers: if matchedlasttime = false - but what about flicker? don't worry about it for now.
        // accelerateIntervention: if later matches > phoneme threshold

        this.matchedLastTime = {};
        bestPath.forEach((entry) => {
            // key needs to be both the word id and the multiword sub index
            // to cover the case of multi-word ids like "1974"
            let multiWordIndex = bestPath.filter((e) => {
                return e.textWordId === entry.textWordId;
            }).indexOf(entry);
            let key = entry.textWordId + "_" + multiWordIndex.toString();
            this.matchedLastTime[key] = { aligned: true, matched: entry.isMatch() };
        });

        let accelerateIntervention:boolean =
            this.getLaterMatches(result.lastTextWord, result.bestTextWord.id, lastMatchEndMSec);

        this.updateWordUploadData(bestPath);
        let resultDetail:PathElementData[] = null;
        if (recognitionResult.endOfFile) {
            resultDetail = bestPath;
        }
        return {id: id, multiWordInternalProgress: multiWordInternalProgress,
                accelerateIntervention: accelerateIntervention, resultDetail:resultDetail,
                resultString: this.logBestPath(bestPath)};

    }

    public makeComparison(recognitionData:IRecognizedSequence):IComparison {

        this.currentRecognitionData = recognitionData.data.slice(0); // TBD or copy better?
        this.currentRecognitionData.unshift({ word: "", start: -1, end: -1, isWord: false }); // null entry
        this.referenceTextData = this.getReferenceText();
        let referenceLength = this.referenceTextData.length;

        this.setupCostArrays();
        let bestTextWord = this.referenceTextData[0].alternates[0][0]; // set to null entry
        let result:IScoreResult = { matchedTextWord: null, exitTextWord: null };

        for (let reference_i = 0; reference_i < referenceLength; reference_i++) {
            // propagate from previous reference (will propagate null if reference_i is 0)
            this.referenceTextData[reference_i].alternates.forEach((alternate) => {
                alternate[0].previousTextWord = result.exitTextWord;
            });
            // NB: bestTextWord is at the multiword alternate level, not the top level.
            result = this.score(this.referenceTextData[reference_i]);

            if (result.matchedTextWord) {
                bestTextWord = result.matchedTextWord;
            }
        }
        let lastTextWord = result.exitTextWord;

        // TBD: is this what we actually want to return?
        return { bestTextWord: bestTextWord, lastTextWord: lastTextWord };
    }

    private updateWordUploadData(bestPath:PathElementData[]):void {
        let postProcessData:IPostProcessData = {};
        let lastEndTime = 0;
        let startTime:number;
        let lastWordId = "";
        // first elapsed time and number of phonemes - must accumulate across multiword
        let pronunciationDictionary = this.pronunciationDictionary;
        bestPath.forEach((entry) => {
            if (entry.bestPathStep !== this.pathStep.INSERTION) {
                if (entry.textWordId !== lastWordId) {
                    startTime = lastEndTime;
                    postProcessData[entry.textWordId] = {elapsedTime: 0, numSyllables: 0};
                }
                if (entry.isMatch()) {
                    postProcessData[entry.textWordId].elapsedTime = entry.recognitionEndMSec - startTime;
                    lastEndTime = entry.recognitionEndMSec;
                    // if numSyllables > 0, we are at the 2nd or later word in a multiword sequence.  Add one
                    // 'pseudo'-syllable to the count to compensate for the fact that separate words will not
                    // be spoken as quickly. Otherwise, multiword sequences can get blue coding indicating disfluency
                    // even when read fluently.
                    let multiWordOffset = postProcessData[entry.textWordId].numSyllables > 0 ? 1 : 0;
                    postProcessData[entry.textWordId].numSyllables += this.getNumSyllables(entry) + multiWordOffset;
                }
                lastWordId = entry.textWordId;
            }

        });
        let priorElapsedTime = 0;
        let priorPunctuation = false;
        // apply this information to the sentenceTextData
        // if the previous item was unmatched, or at the start of the sequence (meaning first word was intervened),
        // elapsed time blue word coding does not apply
        this._sentenceTextData.forEach((sentenceItem:IReferenceTextData) => {
           if (postProcessData[sentenceItem.word.id]) {
               sentenceItem.elapsedTime = postProcessData[sentenceItem.word.id].elapsedTime;
               // red color coding: audio intervention on target or default
               if ((sentenceItem.word.isTarget() || sentenceItem.word.isDefault())
                   && sentenceItem.interventionStatus === InterventionStatus.AUDIO) {
                   sentenceItem.wordColor = ReviewWordColor.NEEDS_WORK;
               }
               // blue color coding: special category word that is intervened or not spoken
               if (sentenceItem.word.isSpecial()
                        && (sentenceItem.elapsedTime === 0
                            || sentenceItem.interventionStatus === InterventionStatus.AUDIO)) {
                   sentenceItem.wordColor = ReviewWordColor.TRY_AGAIN;
               }
               // blue color coding: target word that received visual intervention
               if (sentenceItem.word.isTarget()
                        && (sentenceItem.interventionStatus === InterventionStatus.VISUAL)) {
                   sentenceItem.wordColor = ReviewWordColor.TRY_AGAIN;
               }
               // blue color coding due to elapsed time
               if ((sentenceItem.word.isTarget() || sentenceItem.word.isDefault())
                        && (sentenceItem.interventionStatus === InterventionStatus.NONE)) {
                   let threshold = 1000 + Math.max(0, postProcessData[sentenceItem.word.id].numSyllables - 3) * 250;
                   if (priorPunctuation) {
                       threshold += 200;
                   }
                   // if prior time is zero, either we are at sentence start or previous word was not recognized,
                   // in these cases we do not use the rule
                   if ((priorElapsedTime > 0) && (sentenceItem.elapsedTime > threshold)) {
                       sentenceItem.wordColor = ReviewWordColor.TRY_AGAIN;
                   }
               }
               priorElapsedTime = sentenceItem.elapsedTime;
               priorPunctuation = sentenceItem.word.hasProsodicPunctuation();
           }
        });
    }

    // get reference text from sentence text - null entry + sentence from most recent intervened word, to end.
    // TBD: need to take in new format - CSSsentence + IExportdictionary
    public getReferenceText():IReferenceTextData[] {
        let foundIntervention = false;
        let array = [];
        for (let i = this._sentenceTextData.length - 1; i >= 0; i--) {
            if ((this._sentenceTextData[i].interventionStatus !== InterventionStatus.NONE) && !foundIntervention) {
                foundIntervention = true;
            }

            array.unshift(this._sentenceTextData[i]);
            if (foundIntervention) {
                // i.e. we found an intervention
                break;
            }
        }
        array.unshift({ word: null, alternates:
            [[{ id: "", previousTextWord: null, text: "", isRequired: false, isLastWord: true, costArray: null}]],
            isRequired: false, interventionStatus: InterventionStatus.NONE});
        return array;

    }

    private getLaterMatches(lastTextWord, lastMatchedWordId, lastMatchEndMSec):boolean {
        // traceback from final entry

        let fullPath:PathElementData[] = this.getBestPath(lastTextWord);
        let numSyllables = 0;
        let foundLastMatch:boolean = lastMatchedWordId === ""; // "" means no match at all
        let afterLastMatch = false;
        fullPath.forEach((entry) => {
            if (entry.textWordId === lastMatchedWordId)  {
                foundLastMatch = true;
            }
            if (foundLastMatch && !afterLastMatch && (entry.textWordId !== lastMatchedWordId)) {
                afterLastMatch = true;
            }
            // could argue for counting later words in a multi-word towards the later match, but
            // let's not for now.
            if (afterLastMatch) {
                if (entry.isMatch() && entry.recognitionEndMSec > lastMatchEndMSec) {
                    numSyllables += this.getNumSyllables(entry);
                }
            }

        });

        return (numSyllables >= this.laterMatchSyllableThreshold);
    }

    private getNumSyllables(entry: PathElementData): number {
        // get num phonemes from text word and
        // pronnum (from recognition) -1 since recognitionPronNum is 1-based.
        let numSyllables = 0;
        if (this.pronunciationDictionary[entry.text] &&
            this.pronunciationDictionary[entry.text][entry.recognitionPronNum - 1]) {
            let vowelsInThisEntry = this.pronunciationDictionary[entry.text][entry.recognitionPronNum - 1].split(" ")
                .filter((phoneme) => (this.vowels.indexOf(phoneme) >= 0));
            numSyllables +=
                vowelsInThisEntry.length;
        }
        return numSyllables;
    }

    private setupCostArrays():void {
        let recognitionLength = this.currentRecognitionData.length;

        for (let reference_i = 0; reference_i < this.referenceTextData.length; reference_i++) {
            for (let alternate_i = 0;
                 alternate_i < this.referenceTextData[reference_i].alternates.length; alternate_i++) {
                let alternate = this.referenceTextData[reference_i].alternates[alternate_i];
                for (let sequence_i = 0; sequence_i < alternate.length; sequence_i++) {
                    alternate[sequence_i].costArray =
                        this.alignArray(recognitionLength, (reference_i === 0) && (sequence_i === 0));
                }
            }
        }
    }

    private score(referenceTextItem):IScoreResult {
        let numMatched = 0;
        let matchedTextWord = null;
        let exitTextWord = null;
        let bestCost = Number.MAX_VALUE;
        for (let i = 0; i < referenceTextItem.alternates.length; i++) {
            // in the case of multi-words, need to know how far we got within the alternate sequence
            let alternate = referenceTextItem.alternates[i];
            let result = this.scoreAlternate(alternate);
            if (result.numMatched > numMatched) {
                matchedTextWord = result.matchedTextWord; // last actual match below threshold
                numMatched = result.numMatched;
            }
            // determine the best scoring alternate for propagation
            let lastTextWord = alternate[alternate.length - 1];
            if (lastTextWord.costArray[lastTextWord.costArray.length - 1].cost < bestCost) {
                exitTextWord = lastTextWord;
                bestCost = lastTextWord.costArray[lastTextWord.costArray.length - 1].cost;
            }
        }
        return { matchedTextWord: matchedTextWord, exitTextWord: exitTextWord };
    }

    // have to propagate all the way thru for later (accel intv) scoring anyway.  Need to know:
    //    - score (for comparing alts)
    //    - last MATCHED text word (in winning alt)
    private scoreAlternate(textWordSequence:ITextWord[]):IScoreResult {

        let numMatched = 0;
        let matchedTextWord:ITextWord = null;
        let textWord:ITextWord = null;
        // can't break, must keep scoring to end and propagate.
        for (let i = 0; i < textWordSequence.length; i++) {
            textWord = textWordSequence[i];
            if (i > 0) {
                textWord.previousTextWord = textWordSequence[i - 1];
            }
            let matchedThisReference = this.scoreWord(textWord);
            if (matchedThisReference) {
                numMatched++;
                matchedTextWord = textWord;
            }
        }
        return { numMatched: numMatched, matchedTextWord: matchedTextWord, lastTextWord: textWord };
    }

    private scoreWord(textWord:ITextWord):boolean {
        let recognitionLength = this.currentRecognitionData.length;
        let matchedThisReference = false;

        for (let recognition_i = 0; recognition_i < recognitionLength; recognition_i++) {
            /* diagonal path - do first so it wins in a tie, results in match aligning to last instance of repeat */
            this.updatePathStepCost(textWord, recognition_i, this.pathStep.DIAGONAL);
            this.updatePathStepCost(textWord, recognition_i, this.pathStep.INSERTION);
            this.updatePathStepCost(textWord, recognition_i, this.pathStep.DELETION);
            let entry:PathElementData = textWord.costArray[recognition_i];

            matchedThisReference = matchedThisReference || entry.isMatch();
            /*if (textWord.costArray[recognition_i].bestPathStep === this.pathStep.DELETION) {
                matchedThisReference = false;
            } else if (textWord.costArray[recognition_i].bestPathStep === this.pathStep.DIAGONAL) {
                matchedThisReference = (textWord.costArray[recognition_i].text ===
                    textWord.costArray[recognition_i].recognitionItemId);
            }*/
        }
        return matchedThisReference && (textWord.costArray[recognitionLength - 1].cost < this.threshold);

    }

    private getBestPath(textWord):PathElementData[] {
        let recognition_i:number = this.currentRecognitionData.length - 1;
        let bestPath:PathElementData[] = [];
        // traceback from last good entry
        let entry = textWord.costArray[recognition_i];
        while (entry.bestPathStep !== this.pathStep.UNKNOWN) {
            bestPath.unshift(entry);
            recognition_i += entry.bestPathStep.delta_recognition;

            if (entry.bestPathStep.delta_reference === -1) {
                textWord = textWord.previousTextWord;
            }
            entry = textWord.costArray[recognition_i];

        }
        // last entry will be null so don't need to push it I think?
        return bestPath;
    }

    public logBestPath(bestPath):string {
        let bestPathInfo = "";
        for (let i = 0; i < bestPath.length; i++) {
            bestPathInfo += this.getLogEntry(bestPath[i]) + " ";
        }

        return bestPathInfo;
    }

    public getLogEntry(entry):string {
        let logEntry = "";
        switch (entry.bestPathStep) {
            case this.pathStep.DIAGONAL:
                // M or S
                if (entry.isMatch()) {
                    logEntry = "M:" + entry.textWordId + "_" + entry.textWordId + "_" + entry.text;
                } else {
                    logEntry = "S:" + entry.textWordId + "_" + entry.textWordId + "_" + entry.text +
                               "!=" + entry.recognitionItemId;
                }
                break;
            case this.pathStep.DELETION:
                // D, only need reference (id and text)
                logEntry = "D:" + entry.textWordId + "_" + entry.textWordId + "_" + entry.text;
                break;
            case this.pathStep.INSERTION:
                // I, only need reco
                logEntry = "I:" + entry.recognitionItemId;
                break;
            default:
                break;
        }

        return logEntry;
    }

    public updatePathStepCost(textWord, recognition_i:number, pathStepType:IPathStepType):void {
        let recognitionData = this.currentRecognitionData;
        let thisCost = textWord.costArray;
        let sourceCost = thisCost;
        if (pathStepType.delta_reference === -1) {
            sourceCost = (textWord.previousTextWord) ? textWord.previousTextWord.costArray : null;
        }

        let source_recognition_i = recognition_i + pathStepType.delta_recognition;
        if (!sourceCost || source_recognition_i < 0) {
            return; // not valid, source is out of bounds of the cost array
        }
        let isMatch = false;
        let isOptional = false;
        let isInsertion = false;
        let isLastWord = false;
        let textWordId = "";
        let recognitionItemId = "";
        let text = "";
        let recognitionEndMSec = -1;
        let recognitionPronNum = 1;
        if (pathStepType === this.pathStep.DIAGONAL) {

            isMatch = (textWord.text === recognitionData[recognition_i].word);
            recognitionItemId = recognitionData[recognition_i].word;
            recognitionPronNum = recognitionData[recognition_i].pronNum;
            recognitionEndMSec = recognitionData[recognition_i].end;
        }
        if (pathStepType !== this.pathStep.INSERTION) {
            isOptional = !textWord.isRequired;
            text = textWord.text;
            textWordId = textWord.id;
            isLastWord = textWord.isLastWord;
        } else {
            isInsertion = true;
            recognitionItemId = recognitionData[recognition_i].word;
            recognitionPronNum = recognitionData[recognition_i].pronNum;
            recognitionEndMSec = recognitionData[recognition_i].end;
        }

        let cost = sourceCost[source_recognition_i].cost + this.getCost(isMatch, isOptional, isInsertion);
        if (cost < thisCost[recognition_i].cost) {
            // update with new best values
            thisCost[recognition_i].cost = cost;
            thisCost[recognition_i].bestPathStep = pathStepType;
            thisCost[recognition_i].textWordId = textWordId;
            thisCost[recognition_i].recognitionItemId = recognitionItemId;
            thisCost[recognition_i].recognitionPronNum = recognitionPronNum;
            thisCost[recognition_i].recognitionEndMSec = recognitionEndMSec;
            thisCost[recognition_i].text = text;
            thisCost[recognition_i].isLastWord = isLastWord;

        }
    }

    public getCost(isMatch, isOpt, isInsertion):number {
        if (isInsertion) {
            return 5;
        }
        if (isMatch) {
            return 0;
        }

        if (isOpt) {
            return 5;
        }

        return 1000;
    }

    public alignArray(len, isStart):PathElementData[] {
        let i: number;
        let a:PathElementData[] = [];

        for (i = 0; i < len; i++) {
            if (i === 0 && isStart) {
                // to be discussed on new approach
                a[i] = new PathElementData(0, this.pathStep.UNKNOWN);
            } else {
                // to be discussed on new approach
                a[i] = new PathElementData(Number.MAX_VALUE, this.pathStep.UNKNOWN);
            }
        }
        return (a);
    }

}
